import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Scale } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useStore } from "@/context/StoreContext";
import CountSheet from "@/components/recon/CountSheet";
import ReconList from "@/components/recon/ReconList";
import ReconDetailDialog from "@/components/recon/ReconDetailDialog";

export default function Reconcile() {
  const { user } = useAuth();
  const { active } = useStore();
  const [recs, setRecs] = useState([]);
  const [open, setOpen] = useState(null);
  const canDecide = ["platform_owner", "vendor_owner", "admin", "manager"].includes(user?.role);
  const load = () => api.get("/inventory/reconciliations").then((r) => setRecs(r.data));
  useEffect(() => { load(); }, [active?.store_id]);
  const pending = recs.filter((r) => r.status === "pending" || r.status === "approving");
  const history = recs.filter((r) => !["draft", "pending", "approving"].includes(r.status || "approved"));
  const storeName = active?.store_name || "this store";

  return (
    <div className="p-4 md:p-10 space-y-6" key={active?.store_id}>
      <div>
        <div className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground mb-2 flex items-center gap-1.5">
          <Scale className="w-3 h-3" strokeWidth={1.5} /> Reconcile · {storeName}
        </div>
        <h1 className="font-display text-4xl tracking-tight" data-testid="reconcile-title">Stock Reconciliation</h1>
        <p className="text-muted-foreground mt-2 text-sm">Count stock, save drafts, submit for approval. Stock only changes when a manager approves, and only by the counted difference.</p>
      </div>
      <Tabs defaultValue="count">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="count" data-testid="recon-tab-count">Stock count</TabsTrigger>
          <TabsTrigger value="pending" data-testid="recon-tab-pending">Pending approval ({pending.length})</TabsTrigger>
          <TabsTrigger value="history" data-testid="recon-tab-history">History</TabsTrigger>
        </TabsList>
        <TabsContent value="count"><CountSheet storeName={storeName} onChanged={load} /></TabsContent>
        <TabsContent value="pending">
          <ReconList recs={pending} empty="No counts waiting for approval." onOpen={setOpen} testid="recon-pending-list" />
        </TabsContent>
        <TabsContent value="history">
          <ReconList recs={history} empty="No approved or rejected reconciliations yet." onOpen={setOpen} testid="recon-history-list" />
        </TabsContent>
      </Tabs>
      <ReconDetailDialog recId={open} canDecide={canDecide} onClose={() => setOpen(null)} onDone={load} />
    </div>
  );
}
